<script setup>
import { useRoute, useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import api from '@/app/api/axios.js';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import CFRoundedButton from '@/ui/RoundedButton/CFRoundedButton.vue';
import { useTeamStore } from '#modules/team/stores/team.store.js';
import { useSite } from '#modules/sites/queries/useSite.js';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import CFBadge from '@/ui/Badge/CFBadge.vue';

const route = useRoute();
const router = useRouter();

const teamStore = useTeamStore();

const {
    data: sites,
    isPending,
    isError,
} = useQuery({
    queryKey: ['sites', teamStore.team.id],
    queryFn: async () => {
        const response = await api.get(`/webops/sites`, {
            params: {
                filter: {
                    team_id: teamStore.team.id,
                },
            },
        });

        return response.data;
    },
    retry: 1,
});

const selectedSite = ref(route.params.site);
const { site: currentSite } = useSite();

const goToSite = (siteId, close) => {
    selectedSite.value = siteId;
    close();
    router.push({ name: route.name, params: { site: siteId } });
};

const sortedSites = computed(() => {
    if (!sites.value?.data || !currentSite.value) {
        return [];
    }

    const current = currentSite.value;
    const others = sites.value.data.filter((env) => env.id !== current.id);

    return [current, ...others];
});
</script>

<template>
    <Popover
        v-if="!isPending && !isError && currentSite"
        v-slot="{ open }"
        class="mb-6"
    >
        <PopoverButton
            class="border-primary hover:bg-neutrals-400/75 flex w-full cursor-pointer items-center gap-2 border-b py-6 text-left transition-all ease-in hover:px-4"
            :class="{
                'bg-neutrals-400 px-4': open,
            }"
        >
            <div class="flex max-w-full min-w-0 flex-1 flex-col items-start gap-1">
                <span class="max-w-full truncate text-xl font-bold">
                    {{ currentSite.attributes.name }}
                </span>
                <span class="max-w-full truncate text-sm text-neutral-600">
                    {{ currentSite.attributes.domain }}
                </span>
                <CFBadge
                    size="xs"
                    color="neutral"
                >
                    {{ $t(`sites.languages.${currentSite.attributes.language}`) }}
                </CFBadge>
            </div>
            <CFIcon
                name="arrow-down1"
                class="size-4 shrink-0"
            />
        </PopoverButton>

        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <PopoverPanel
                v-slot="{ close }"
                class="bg-grey-light absolute inset-0 z-10"
            >
                <div class="flex flex-col px-6 pt-9 pb-6">
                    <CFRoundedButton
                        icon="close"
                        color="secondary"
                        @click="close()"
                    />

                    <div class="mt-4">
                        <button
                            v-for="site in sortedSites"
                            :key="site.id"
                            type="button"
                            class="border-primary hover:bg-neutrals-400/75 flex w-full cursor-pointer flex-col items-start justify-center gap-1 border-b px-0 py-6 text-left transition-all ease-in hover:px-4"
                            :class="{
                                'bg-neutrals-400 px-4': site.id === selectedSite,
                            }"
                            @click="goToSite(site.id, close)"
                        >
                            <span class="max-w-full truncate text-xl font-bold">
                                {{ site.attributes.name }}
                            </span>
                            <span class="max-w-full truncate text-sm text-neutral-600">
                                {{ site.attributes.domain }}
                            </span>
                            <CFBadge
                                size="xs"
                                color="neutral"
                            >
                                {{ $t(`sites.languages.${site.attributes.language}`) }}
                            </CFBadge>
                        </button>
                    </div>
                </div>
            </PopoverPanel>
        </Transition>
    </Popover>
</template>
